import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  Paper,
  TextField,
  CircularProgress,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CloseIcon from '@mui/icons-material/Close';

interface RestaurantNumpadModalProps {
  open: boolean;
  productName: string;
  productImage?: string;
  onConfirm: (quantity: number, multiplier: number) => void;
  onCancel: () => void;
}

const MULTIPLIER_VALUES = [2, 3, 4, 5];

const RestaurantNumpadModal: React.FC<RestaurantNumpadModalProps> = ({
  open,
  productName,
  productImage,
  onConfirm,
  onCancel,
}) => {
  const [displayValue, setDisplayValue] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDisplayValue('1');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleNumberClick = (num: string) => {
    if (isSubmitting) return;
    if (displayValue === '0' || displayValue === '1') {
      setDisplayValue(num);
    } else {
      setDisplayValue(displayValue + num);
    }
  };

  const handleDecimalClick = () => {
    if (isSubmitting) return;
    if (displayValue.includes('.')) return;
    setDisplayValue(displayValue + '.');
  };

  const handleHalfClick = () => {
    if (isSubmitting) return;
    const currentNum = parseFloat(displayValue || '0');
    const newValue = Math.floor(currentNum) + 0.5;
    setDisplayValue(newValue.toString());
  };

  const handleBackspace = () => {
    if (isSubmitting) return;
    if (displayValue.length > 1) {
      setDisplayValue(displayValue.slice(0, -1));
    } else {
      setDisplayValue('1');
    }
  };

  const handleClear = () => {
    if (isSubmitting) return;
    setDisplayValue('1');
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    const quantity = parseFloat(displayValue);
    if (quantity > 0 && quantity <= 9999) {
      setIsSubmitting(true);
      onConfirm(quantity, 1);
    }
  };

  const handleMultiplierClick = (multiplier: number) => {
    if (isSubmitting) return;
    const quantity = parseFloat(displayValue);
    if (quantity > 0 && quantity <= 9999) {
      setIsSubmitting(true);
      onConfirm(quantity, multiplier);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isSubmitting) return;
    if (event.key === 'Enter') {
      handleConfirm();
    } else if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Backspace') {
      handleBackspace();
    } else if (event.key >= '0' && event.key <= '9') {
      handleNumberClick(event.key);
    } else if (event.key === '.' || event.key === ',') {
      handleDecimalClick();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      onKeyDown={handleKeyDown}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '460px',
          p: 0,
          overflow: 'hidden',
        },
      }}
    >
      {/* Ürün Bilgisi Başlık */}
      <Box
        sx={{
          bgcolor: isSubmitting ? 'success.main' : 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background-color 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          {isSubmitting && (
            <CircularProgress size={24} sx={{ color: 'white', flexShrink: 0 }} />
          )}
          {!isSubmitting && productImage && (
            <Box
              component="img"
              src={productImage}
              alt={productName}
              sx={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 1.5,
                border: '2px solid rgba(255,255,255,0.3)',
              }}
            />
          )}
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isSubmitting ? 'Ekleniyor...' : productName}
          </Typography>
        </Box>
        {!isSubmitting && (
          <IconButton onClick={onCancel} size="small" sx={{ color: 'white', ml: 1 }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Numpad + Çarpan İçerik */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: 'background.paper',
          opacity: isSubmitting ? 0.5 : 1,
          pointerEvents: isSubmitting ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Sol taraf: Numpad */}
          <Box sx={{ flex: 1 }}>
            {/* Miktar Gösterimi */}
            <TextField
              fullWidth
              value={displayValue}
              InputProps={{
                readOnly: true,
                sx: {
                  fontSize: '2.2rem',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  '& input': {
                    textAlign: 'center',
                    color: 'primary.main',
                  },
                },
              }}
              sx={{ mb: 2.5 }}
            />

            {/* Numerik Keypad */}
            <Grid container spacing={1.5}>
              {/* Satır 1: 7, 8, 9, Sil */}
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('7')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  7
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('8')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  8
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('9')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  9
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" color="error" onClick={handleBackspace} disabled={isSubmitting}
                  sx={{ height: 52, fontWeight: 'bold' }}>
                  <BackspaceIcon />
                </Button>
              </Grid>

              {/* Satır 2: 4, 5, 6, Temizle */}
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('4')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  4
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('5')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  5
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('6')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  6
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" color="warning" onClick={handleClear} disabled={isSubmitting}
                  sx={{ fontSize: '1rem', height: 52, fontWeight: 'bold' }}>
                  C
                </Button>
              </Grid>

              {/* Satır 3: 1, 2, 3, +½ */}
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('1')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  1
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('2')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  2
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('3')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  3
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" color="secondary" onClick={handleHalfClick} disabled={isSubmitting}
                  sx={{ fontSize: '1.1rem', height: 52, fontWeight: 'bold' }}>
                  +½
                </Button>
              </Grid>

              {/* Satır 4: ., 0, Ekle (2 sütun) */}
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={handleDecimalClick} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  .
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button fullWidth variant="contained" onClick={() => handleNumberClick('0')} disabled={isSubmitting}
                  sx={{ fontSize: '1.4rem', height: 52, fontWeight: 'bold', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                  0
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="contained" color="success" onClick={handleConfirm} disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddShoppingCartIcon />}
                  sx={{ fontSize: '1rem', height: 52, fontWeight: 'bold', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
                  {isSubmitting ? 'Ekleniyor...' : 'Ekle'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Sağ taraf: Çarpan butonları */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: 80,
              flexShrink: 0,
            }}
          >
            {MULTIPLIER_VALUES.map((m) => (
              <Button
                key={m}
                fullWidth
                variant="outlined"
                onClick={() => handleMultiplierClick(m)}
                disabled={isSubmitting}
                sx={{
                  flex: 1,
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  minHeight: 60,
                  borderWidth: 2,
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  '&:hover': {
                    bgcolor: 'secondary.main',
                    color: 'white',
                    borderWidth: 2,
                    borderColor: 'secondary.main',
                  },
                }}
              >
                x{m}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default RestaurantNumpadModal;
